import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { Order } from '@src/orders/domain/Order';
import { type PermitApplication } from '@src/orders/domain/PermitApplication';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import type { fields } from '@src/permitField/Fields';
import { TrailersFormName } from '@src/permitField/TrailerInformationFields';
import { PermitSection } from '@src/permits/PermitSection';
import { useTranslation } from 'react-i18next';

interface Props {
  name: string;
  category: keyof typeof fields;
  permitApplication: PermitApplication;
  order: Order;
}

const SECTION_WITH_NESTED_FIELDS = [TrailersFormName, AxleFormName];

export const PermitCategory = ({
  name,
  category,
  permitApplication,
  order,
}: Props) => {
  const { t } = useTranslation();

  const fieldsBySections = permitApplication.getFieldsBySection(category);

  if (fieldsBySections.size === 0) return null;

  return (
    <Stack key={name} className={categoryFields}>
      <Typography variant="subtitle2" className={categoryTitle}>
        {t(`orderSections.${name}`)}
      </Typography>

      {Array.from(fieldsBySections).map(([section, fields]) => {
        if (fields.length === 0) return null;
        return (
          <PermitSection
            key={section}
            section={section}
            fields={fields}
            permitApplication={permitApplication}
            order={order}
            nested={SECTION_WITH_NESTED_FIELDS.includes(section)}
          />
        );
      })}
    </Stack>
  );
};

const categoryTitle = css(() => ({
  borderBottom: '1px solid',
  borderColor: `${theme.palette.surface.outlineVariant}`,
  paddingBottom: `${theme.spacing['2']}`,
}));

const categoryFields = css(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
}));
